import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { type ContractExtensionDto, type OptionDto } from "../../../../../autogen/bff-api";
import { ExtensionFields, type ExtensionDraft } from "../../../../../common/extension/ExtensionFields";
import { DEFAULT_EXTENSION } from "../../../../../common/extension/extensionRules";
import { useDisplayDate } from "../../../../../common/formatting/useDisplayDate";
import { ISODateFormControl } from "../../../../../common/input/DateTimeSelectors/ISODateFormControl";
import { usePendingSaves } from "../../../../../common/input/pendingSaves";
import { useDebouncedSave } from "../../../../../common/input/useDebouncedSave";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import {
  type ContractState,
  OPTION_CHANGED_ELSEWHERE,
  optionEditChangedElsewhere,
  optionEditFailed,
  refusedFieldErrors,
  refusedOptionEditFieldErrors,
} from "../../../../../common/redux/reducers/contractReducer";
import {
  editContractThunk,
  type EditContractCommand,
} from "../../../../../common/redux/thunks/contract/edit-contract-thunk";
import { toLastSeen } from "../../../../../common/redux/thunks/contract/lastSeenOption";
import { ConfirmationNotCompleted, useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { useAlreadyDecidedMessage } from "../../../options/alreadyDecided";
import { exerciseDeadline, isBeforeContractStart } from "../../../options/deadlineBeforeContractStart";
import { firstDeclined, isDecided, reachesInto } from "../../../options/extensionStatus";
import {
  endsNoLaterThanContract,
  FIRST_SUPPORTED_DATE,
  type HandSetDate,
  handSetDateProblems,
  hasHandSetDateProblem,
  isSavableExtension,
  LAST_SUPPORTED_DATE,
  optionKind,
} from "../../../options/optionKind";
import {
  OptionDeadlinePassed,
  OptionDuration,
  OptionLapsedBecause,
  OptionPeriod,
  OptionStatus,
} from "../../../options/OptionPeriod";
import { type ExtensionName, useExtensionName } from "../../../options/extensionName";
import { withUnsavedPeriods } from "../../../options/unsavedOptionPeriods";
import { type HeldValues } from "../../heldExtensionValues";
import { useContractState } from "../../useContractState";
import { useDeadlineBeforeStartExplanation } from "./dates/useDeadlineBeforeStartRefusal";
import { EXTENSIONS_OVERLAP, useExtensionsOverlapExplanation } from "./dates/useExtensionsOverlapExplanation";
import { useLastSupportedDateExplanation } from "./dates/useLastSupportedDateRefusal";
import { WOULD_LAPSE } from "./dates/useWouldLapseExplanation";

const SEEDED_DEADLINE_MONTHS = [3, 2, 1];
const OUTSIDE_SUPPORTED_RANGE = "zz_FieldErrorCode_ExtensionDateOutsideSupportedRange";

export const ContractOptions = () => {
  const { t } = useTranslation();
  const contractState = useContractState();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { show: confirm, modal: confirmationModal } = useConfirmationModal();
  const alreadyDecided = useAlreadyDecidedMessage(contractState.contract.dataFields.options ?? []);

  const savedOptions = contractState.contract.dataFields.options;
  const contractEndDate = contractState.contract.dataFields.endDate?.date ?? undefined;
  const contractStart = contractState.contract.dataFields.startDate;
  const [options, setOptions] = useState<OptionDto[]>();
  const decidedElsewhere = contractState.optionsDecidedElsewhere ?? [];

  useEffect(() => {
    if (!options) setOptions(savedOptions ?? []);
  }, [savedOptions, options]);

  const shownOptions = withUnsavedPeriods(options ?? [], savedOptions, contractEndDate);
  const declined = firstDeclined(shownOptions);
  const nameOf = useExtensionName(shownOptions);
  const wouldLapse = (candidate: OptionDto) => {
    const local = (options ?? []).map((o) => (o.id === candidate.id ? candidate : o));
    const unsaved = savedOptions?.filter((saved) => saved.id !== candidate.id);
    const placed = withUnsavedPeriods(local, unsaved, contractEndDate).find((o) => o.id === candidate.id);
    return !!placed && reachesInto(placed, declined);
  };
  const refusedLapses = refusedFieldErrors(contractState, "EditOption").filter((error) => error.code === WOULD_LAPSE);
  const lapsesBecauseOf = (option: OptionDto): OptionDto | undefined => {
    if (wouldLapse(option)) return declined;
    const refusal = refusedLapses.find((error) => error.params?.optionId === option.id);
    if (!refusal) return undefined;
    const declinedId = refusal.params?.declinedOptionId;
    return shownOptions.find((o) => o.id === declinedId) ?? { id: declinedId, name: refusal.params?.declinedName };
  };
  const explainPastLastDate = useLastSupportedDateExplanation();
  const refusalsOf = (option: OptionDto) => refusedOptionEditFieldErrors(contractState, option.id);
  const pastLastDate = (option: OptionDto): string | null => {
    const reason = explainPastLastDate(refusalsOf(option));
    return reason && t("{{reason}} It is not saved.", { reason });
  };
  const refusedOutsideRange = (option: OptionDto): HandSetDate[] =>
    refusalsOf(option)
      .filter((error) => error.code === OUTSIDE_SUPPORTED_RANGE)
      .map((error) => error.field as HandSetDate);
  const explainOverlap = useExtensionsOverlapExplanation();
  const refusedOverlap = (option: OptionDto): RefusedOverlap | null => {
    const overlap = refusalsOf(option).find((error) => error.code === EXTENSIONS_OVERLAP);
    if (!overlap) return null;
    return { startsInside: overlap.params?.laterId === option.id, message: explainOverlap(overlap) };
  };
  const explainDeadlineBeforeStart = useDeadlineBeforeStartExplanation();
  const refusedDeadlineBeforeStart = (option: OptionDto) =>
    explainDeadlineBeforeStart(refusedOptionEditFieldErrors(contractState, option.id));
  const failedWithoutReason = (option: OptionDto) =>
    optionEditFailed(contractState, option.id) &&
    !optionEditChangedElsewhere(contractState, option.id) &&
    refusalsOf(option).length === 0;
  const shownFor = (option: OptionDto, shown = shownOptions) => shown.find((o) => o.id === option.id) ?? option;
  const deadlineIsBeforeStart = (option: OptionDto, shown = shownOptions) =>
    isBeforeContractStart(exerciseDeadline(option, shownFor(option, shown)), contractStart);

  const lastSeen = useRef(new Map<string, OptionDto>());
  const addedHere = useRef(new Set<string>());
  const rememberLastSeen = (option: OptionDto) => {
    if (!option.id || addedHere.current.has(option.id) || lastSeen.current.has(option.id)) return;
    const before = options?.find((o) => o.id === option.id);
    if (before) lastSeen.current.set(option.id, before);
  };

  const waitingToSend = useRef(new Map<string, OptionDto>());
  const sending = useRef(new Map<string, Promise<void>>());
  const queueOwners = useRef(new Map<string, object>());
  const heldOnPage = useRef(new Set<string>());
  const refusedOnPage = useRef(new Set<string>());
  const latestOptions = useRef(options);
  latestOptions.current = options;
  const latestStored = useRef(savedOptions);
  latestStored.current = savedOptions;
  const replaceDatesLocally = useCallback((id: string, dates: OptionDates) => {
    setOptions((current) => current?.map((o) => (o.id === id ? { ...o, ...dates } : o)));
  }, []);
  const recheckHeld = useRef<(contract: ContractState["contract"]) => Promise<unknown>>(async () => undefined);

  const sendOption = useCallback(
    async (option: OptionDto) => {
      const seen = option.id ? lastSeen.current.get(option.id) : undefined;
      const action = await dispatch(
        editContractThunk({ command: { type: "EditOption", option, lastSeen: seen && toLastSeen(seen) } })
      );
      const result = editContractThunk.fulfilled.match(action) ? action.payload : null;
      if (option.id && result && isSaved(result, option.id)) {
        latestStored.current = result.contract.dataFields.options;
        const stored = result.contract.dataFields.options?.find((o) => o.id === option.id);
        const storedDates = stored?.datesSetByHand && option.datesSetByHand ? datesOf(stored) : undefined;
        const datesDiffer = !!storedDates && !sameDates(storedDates, datesOf(option));
        lastSeen.current.set(option.id, datesDiffer ? { ...option, ...storedDates } : option);
        const local = latestOptions.current?.find((o) => o.id === option.id);
        const unchangedSinceSent =
          !!local?.datesSetByHand && sameDates(datesOf(local), datesOf(option)) && !waitingToSend.current.has(option.id);
        if (storedDates && datesDiffer && unchangedSinceSent) replaceDatesLocally(option.id, storedDates);
        await recheckHeld.current(result.contract);
      }
    },
    [dispatch, replaceDatesLocally]
  );

  const drainAfter = useCallback(
    (id: string, before?: () => Promise<unknown>) => {
      const owner = {};
      queueOwners.current.set(id, owner);
      const ownsQueue = () => queueOwners.current.get(id) === owner;
      const drain = (async () => {
        try {
          if (before) await before();
          for (let next = waitingToSend.current.get(id); next && ownsQueue(); next = waitingToSend.current.get(id)) {
            waitingToSend.current.delete(id);
            await sendOption(next);
          }
        } finally {
          if (ownsQueue()) {
            sending.current.delete(id);
            queueOwners.current.delete(id);
          }
        }
      })();
      if (ownsQueue()) sending.current.set(id, drain);
      return drain;
    },
    [sendOption]
  );

  const editOption = useCallback(
    async (option: OptionDto) => {
      const id = option.id;
      if (!id) return sendOption(option);
      waitingToSend.current.set(id, option);
      return sending.current.get(id) ?? drainAfter(id);
    },
    [sendOption, drainAfter]
  );
  const editOptionDebounced = useDebouncedSave(editOption);

  recheckHeld.current = async (contract: ContractState["contract"]) => {
    const local = latestOptions.current ?? [];
    const shown = withUnsavedPeriods(local, contract.dataFields.options, contract.dataFields.endDate?.date ?? undefined);
    const nowValid = local.filter(
      (option) =>
        !!option.id &&
        heldOnPage.current.has(option.id) &&
        !isBeforeContractStart(exerciseDeadline(option, shownFor(option, shown)), contract.dataFields.startDate)
    );
    for (const option of nowValid) if (option.id) heldOnPage.current.delete(option.id);
    await Promise.all(nowValid.map(editOption));
  };

  const everySendSettled = useCallback(async () => {
    const awaited = new Set<Promise<void>>();
    const unawaited = () => [...sending.current.values()].filter((drain) => !awaited.has(drain));
    for (let open = unawaited(); open.length > 0; open = unawaited()) {
      open.forEach((drain) => awaited.add(drain));
      await Promise.all(open);
    }
  }, []);

  const pendingSaves = usePendingSaves();
  useEffect(() => {
    const held: HeldValues = {
      flush: () => everySendSettled(),
      recheck: (contract) => recheckHeld.current(contract),
      isHolding: () =>
        (latestOptions.current ?? []).some(
          (option) => !!option.id && (heldOnPage.current.has(option.id) || refusedOnPage.current.has(option.id))
        ),
    };
    pendingSaves?.add(held);
    return () => {
      pendingSaves?.delete(held);
    };
  }, [pendingSaves, everySendSettled]);

  const replaceLocally = (option: OptionDto) =>
    setOptions((current) => current?.map((o) => (o.id === option.id ? option : o)));

  const changeOption = (option: OptionDto) => {
    rememberLastSeen(option);
    replaceLocally(option);
    if (option.id) heldOnPage.current.delete(option.id);
    if (option.id) refusedOnPage.current.delete(option.id);
    const refusedOnThisPage =
      (!!option.extension && !option.datesSetByHand && !isSavableExtension(option.extension)) ||
      (!!option.datesSetByHand && hasHandSetDateProblem(option));
    if (refusedOnThisPage) {
      if (option.id) refusedOnPage.current.add(option.id);
      return;
    }
    if (deadlineIsBeforeStart(option)) {
      if (option.id) heldOnPage.current.add(option.id);
      return;
    }
    if (wouldLapse(option)) {
      editOptionDebounced.cancel();
      return;
    }
    editOptionDebounced(option);
  };

  const saveOptionNow = (option: OptionDto) => {
    editOptionDebounced.flush();
    rememberLastSeen(option);
    replaceLocally(option);
    if (option.id) heldOnPage.current.delete(option.id);
    if (option.id) refusedOnPage.current.delete(option.id);
    if (wouldLapse(option)) return;
    if (option.datesSetByHand && option.id) setDatesByHand(option, option.id);
    else editOption(option);
  };

  const withStoredDatesShown = (local: OptionDto, switched: OptionDto, id: string) => {
    const calculated = latestStored.current?.find((o) => o.id === id);
    const seen = lastSeen.current.get(id);
    if (seen && !seen.datesSetByHand && calculated && !calculated.datesSetByHand) {
      lastSeen.current.set(id, { ...seen, ...datesOf(calculated) });
    }
    const rebuilt = withStoredDates(local, switched, latestStored.current);
    if (!sameDates(datesOf(rebuilt), datesOf(local))) replaceDatesLocally(id, datesOf(rebuilt));
    return rebuilt;
  };

  const setDatesByHand = (switched: OptionDto, id: string) => {
    const othersSending = [...sending.current].filter(([other]) => other !== id).map(([, drain]) => drain);
    if (othersSending.length === 0) return editOption(withStoredDatesShown(switched, switched, id));
    const ownSending = sending.current.get(id);
    return drainAfter(id, async () => {
      await Promise.all([...othersSending, ownSending]);
      editOptionDebounced.flush();
      const local = latestOptions.current?.find((o) => o.id === id);
      if (local?.datesSetByHand) waitingToSend.current.set(id, withStoredDatesShown(local, switched, id));
    });
  };

  const addOption = () => {
    const id = uuid();
    const option: OptionDto = {
      id,
      name: unusedExtensionName(options ?? [], (number) => String(t("Extension {{number}}", { number }))),
      description: "",
      isExecuted: false,
      isDeclined: false,
      extension: DEFAULT_EXTENSION,
      datesSetByHand: false,
    };
    const shown = withUnsavedPeriods([...(options ?? []), option], savedOptions, contractEndDate);
    const seeded = SEEDED_DEADLINE_MONTHS.map((exerciseDeadlineMonths) => ({
      ...option,
      extension: { ...DEFAULT_EXTENSION, exerciseDeadlineMonths },
    })).find((candidate) => !deadlineIsBeforeStart(candidate, shown));
    const added = seeded ?? option;
    addedHere.current.add(id);
    setOptions([...(options ?? []), added]);
    if (seeded) editOption(added);
  };

  const ownSavesSettled = async (id: string) => {
    await editOptionDebounced.flush();
    for (let inFlight = sending.current.get(id); inFlight; inFlight = sending.current.get(id)) await inFlight;
  };

  const confirmRemove = (option: OptionDto) =>
    confirm({
      title: String(t("Delete {{name}}?", { name: nameOf(option) })),
      description: t("This cannot be undone."),
      confirmButtonText: String(t("Delete")),
      confirmButtonColor: "red",
      onConfirmed: async () => {
        if (!option.id) return;
        await ownSavesSettled(option.id);
        const outcome = await save({ type: "RemoveOption", id: option.id });
        if (outcome.decided) {
          toast({ title: alreadyDecided(outcome.decided), status: "info" });
          return;
        }
        if (!outcome.saved) {
          keepOpenWithError(t("The option was not deleted. Reload the page and try again."));
        }
        setOptions((current) => current?.filter((o) => o.id !== option.id));
      },
    });

  const save = async (
    command: EditContractCommand & { type: "RemoveOption" }
  ): Promise<{ saved: boolean; decided?: OptionDto }> => {
    try {
      const result = await dispatch(editContractThunk({ command })).unwrap();
      if (result?.optionsDecidedElsewhere?.includes(command.id)) {
        return { saved: false, decided: result.contract.dataFields.options?.find((o) => o.id === command.id) };
      }
      return { saved: !!result && !result.errors.includes(command.type) };
    } catch {
      return { saved: false };
    }
  };

  const keepOpenWithError = (title: string): never => {
    toast({ title, status: "error" });
    throw new ConfirmationNotCompleted(title);
  };

  return (
    <>
      {confirmationModal}
      <Flex flexDirection="column">
        {options?.map((option) => {
          const shown = shownFor(option);
          const kind =
            isDecided(shown) || (option.id && decidedElsewhere.includes(option.id)) ? "locked" : optionKind(option);
          const lengthWouldLapse =
            kind === "earlier" && wouldLapse({ ...option, extension: DEFAULT_EXTENSION, datesSetByHand: false });
          return (
            <Flex
              key={option.id}
              flexDirection="column"
              backgroundColor="smBackground"
              rounded="md"
              p="2"
              my="2"
              data-testid={`option-${option.id}`}
            >
              {kind === "locked" || kind === "earlier" ? (
                <Stack spacing="1">
                  <Text fontWeight="bold" fontSize="sm">
                    {nameOf(shown)}
                  </Text>
                  {shown.description && (
                    <Box as="pre" fontFamily="body" whiteSpace="pre-wrap" fontSize="sm">
                      {shown.description}
                    </Box>
                  )}
                  <OptionDuration option={shown} />
                  <OptionPeriod option={shown} />
                </Stack>
              ) : (
                <EditableOption
                  option={option}
                  shown={shown}
                  lapsesBecauseOf={lapsesBecauseOf(option)}
                  refusedDeadlineBeforeStart={refusedDeadlineBeforeStart(option)}
                  refusedOverlap={refusedOverlap(option)}
                  pastLastDate={pastLastDate(option)}
                  refusedOutsideRange={refusedOutsideRange(option)}
                  nameOf={nameOf}
                  contractEndDate={contractEndDate}
                  onChange={changeOption}
                  onModeChange={saveOptionNow}
                />
              )}
              {kind === "earlier" && (
                <Box mt="2" p="2" borderWidth="1px" borderColor="smWarningSoftBorder" bg="smWarningSoft" rounded="md">
                  <Text fontSize="sm" mb="2">
                    {t("This option was registered without a length. Give it a length, or set the dates yourself, to change it.")}
                  </Text>
                  <Flex gap="2" wrap="wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="teal"
                      onClick={() => saveOptionNow({ ...option, extension: DEFAULT_EXTENSION, datesSetByHand: false })}
                      isDisabled={lengthWouldLapse}
                    >
                      {t("Give a length")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="teal"
                      onClick={() => saveOptionNow({ ...option, datesSetByHand: true })}
                    >
                      {t("Set the dates yourself")}
                    </Button>
                  </Flex>
                  {lengthWouldLapse && declined && <WouldLapse name={nameOf(declined)} />}
                </Box>
              )}
              {kind !== "locked" && <OptionDeadlinePassed option={shown} />}
              {kind !== "locked" && optionEditChangedElsewhere(contractState, option.id) && (
                <Text fontSize="sm" color="smError" pt="1" data-testid="option-changed-elsewhere">
                  {t(OPTION_CHANGED_ELSEWHERE)}
                </Text>
              )}
              {kind !== "locked" && failedWithoutReason(option) && (
                <Text fontSize="sm" color="smError" pt="1" data-testid="option-update-failed">
                  {t("Update failed")}
                </Text>
              )}
              <Flex justifyContent="space-between" alignItems="center" pt="2" gap="2" wrap="wrap">
                {kind === "locked" ? (
                  <Box py="2">
                    {isDecided(shown) ? (
                      <OptionStatus option={shown} />
                    ) : (
                      <Text fontSize="sm">{alreadyDecided(shown)}</Text>
                    )}
                    <OptionLapsedBecause option={shown} options={shownOptions} />
                    {option.id && decidedElsewhere.includes(option.id) && (
                      <Text fontSize="sm" color="smMuted" pt="1" data-testid="option-decided-elsewhere">
                        {t("This extension was decided while you were editing, so your changes to it were not saved.")}
                      </Text>
                    )}
                  </Box>
                ) : (
                  <Text fontSize="sm" color="smMuted" py="2" data-testid="option-decided-on-contract-page">
                    {t("Exercise or decline the extension from the contract page.")}
                  </Text>
                )}
                {kind !== "locked" && (
                  <IconButton
                    aria-label={String(t("Delete {{name}}", { name: nameOf(option) }))}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<Icon as={FaTrash} />}
                    onClick={() => confirmRemove(option)}
                  />
                )}
              </Flex>
            </Flex>
          );
        })}
        <Box pt="5">
          <Button
            variant="outline"
            size="sm"
            colorScheme="teal"
            rightIcon={<Icon as={FaPlus} />}
            onClick={addOption}
            isDisabled={!!declined}
          >
            {t("Add extension")}
          </Button>
          {declined && (
            <Text fontSize="sm" color="smMuted" pt="1" data-testid="option-add-after-decline">
              {t("{{name}} was not exercised, so the contract cannot be extended past it. A new extension would lapse.", {
                name: nameOf(declined),
              })}
            </Text>
          )}
        </Box>
      </Flex>
    </>
  );
};

type OptionDates = Pick<OptionDto, "startDate" | "deadline" | "endDate">;

const datesOf = ({ startDate, deadline, endDate }: OptionDto): OptionDates => ({ startDate, deadline, endDate });

const sameDates = (a: OptionDates, b: OptionDates): boolean =>
  a.startDate === b.startDate && a.deadline === b.deadline && a.endDate === b.endDate;

const withStoredDates = (local: OptionDto, switched: OptionDto, stored: OptionDto[] | null | undefined): OptionDto => {
  const calculated = stored?.find((o) => o.id === local.id);
  if (!calculated || calculated.datesSetByHand) return local;
  const untouched = (date: keyof OptionDates) => (local[date] === switched[date] ? calculated[date] ?? local[date] : local[date]);
  return { ...local, startDate: untouched("startDate"), deadline: untouched("deadline"), endDate: untouched("endDate") };
};

const isSaved = (result: ContractState | null, optionId: string): boolean =>
  !!result &&
  !(result.failedEdits ?? []).some((failed) => failed.command.type === "EditOption" && failed.command.option.id === optionId);

const WouldLapse = ({ name }: { name: string }) => {
  const { t } = useTranslation();
  return (
    <Text fontSize="sm" color="smError" pt="1" data-testid="option-would-lapse">
      {t("{{name}} was not exercised, so this extension would lapse. It is not saved.", { name })}
    </Text>
  );
};

interface RefusedOverlap {
  message: string;
  startsInside: boolean;
}

interface EditableOptionProps {
  option: OptionDto;
  shown: OptionDto;
  lapsesBecauseOf?: OptionDto;
  refusedDeadlineBeforeStart: string | null;
  refusedOverlap: RefusedOverlap | null;
  pastLastDate: string | null;
  refusedOutsideRange: HandSetDate[];
  nameOf: ExtensionName;
  contractEndDate?: string;
  onChange: (option: OptionDto) => void;
  onModeChange: (option: OptionDto) => void;
}

const EditableOption = ({
  option,
  shown,
  lapsesBecauseOf,
  refusedDeadlineBeforeStart,
  refusedOverlap,
  pastLastDate,
  refusedOutsideRange,
  nameOf,
  contractEndDate,
  onChange,
  onModeChange,
}: EditableOptionProps) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const contractStart = useContractState().contract.dataFields.startDate;
  const extension = option.extension ?? DEFAULT_EXTENSION;
  const deadline = exerciseDeadline(option, shown);
  const deadlineBeforeStart = isBeforeContractStart(deadline, contractStart);
  const changeExtension = (change: Partial<ExtensionDraft>) =>
    onChange({ ...option, extension: { ...extension, ...change } as ContractExtensionDto });
  const dateProblems = handSetDateProblems(option);
  const startLapses = !!lapsesBecauseOf && reachesInto({ ...option, endDate: null }, lapsesBecauseOf);
  const lapseReason = lapsesBecauseOf ? <WouldLapse name={nameOf(lapsesBecauseOf)} /> : null;
  const pastLastDateReason = pastLastDate ? (
    <Text fontSize="sm" color="smError" pt="1" data-testid="option-past-last-date">
      {pastLastDate}
    </Text>
  ) : null;
  const isOutsideRange = (date: HandSetDate) =>
    dateProblems.outsideSupportedRange.includes(date) || refusedOutsideRange.includes(date);
  const outsideRangeMessage = t("Choose a date between {{first}} and {{last}}.", {
    first: displayDate(FIRST_SUPPORTED_DATE),
    last: displayDate(LAST_SUPPORTED_DATE),
  });

  return (
    <>
      <Input
        backgroundColor="smBackground"
        borderColor="smInputBorder"
        size="sm"
        rounded="md"
        placeholder={`${t("Title")}...`}
        value={option.name}
        onChange={(e) => onChange({ ...option, name: e.target.value })}
      />
      <Textarea
        backgroundColor="smBackground"
        borderColor="smInputBorder"
        size="sm"
        rounded="md"
        mt="2"
        placeholder={`${t("Description")}...`}
        value={option.description}
        onChange={(e) => onChange({ ...option, description: e.target.value })}
      />
      <RadioGroup
        mt="3"
        size="sm"
        value={option.datesSetByHand ? "byHand" : "duration"}
        onChange={(mode) =>
          onModeChange(
            mode === "byHand"
              ? {
                  ...option,
                  datesSetByHand: true,
                  startDate: shown.startDate,
                  endDate: shown.endDate,
                  deadline: shown.deadline,
                }
              : { ...option, datesSetByHand: false, extension }
          )
        }
      >
        <Flex gap="4" wrap="wrap">
          <Radio value="duration">{t("Give a length")}</Radio>
          <Radio value="byHand">{t("Set the dates yourself")}</Radio>
        </Flex>
      </RadioGroup>
      {!option.datesSetByHand && lapseReason}
      {option.datesSetByHand ? (
        <Box key="byHand">
          <Box pt="2">
            <ISODateFormControl
              id={`${option.id}-start-date`}
              title={t("Start date")}
              helperText=""
              defaultDate={option.startDate ?? undefined}
              errorMessage={
                isOutsideRange("startDate")
                  ? outsideRangeMessage
                  : startLapses
                    ? lapseReason
                    : refusedOverlap?.startsInside
                      ? refusedOverlap.message
                      : null
              }
              onChange={(date) => onChange({ ...option, startDate: date ?? null })}
              isRequiredButNotProvided={false}
              savesTypingOnLeave
            />
          </Box>
          <Box pt="2">
            <ISODateFormControl
              id={`${option.id}-deadline`}
              title={t("Option execution deadline")}
              helperText=""
              defaultDate={option.deadline ?? undefined}
              errorMessage={
                isOutsideRange("deadline")
                  ? outsideRangeMessage
                  : dateProblems.deadlineAfterStart
                    ? t("The deadline to exercise cannot be after the start date.")
                    : deadlineBeforeStart
                      ? t("The deadline cannot be before the contract starts on {{start}}", {
                          start: displayDate(contractStart ?? ""),
                        })
                      : null
              }
              onChange={(date) => onChange({ ...option, deadline: date ?? null })}
              isRequiredButNotProvided={false}
              savesTypingOnLeave
            />
          </Box>
          <Box pt="2">
            <ISODateFormControl
              id={`${option.id}-end-date`}
              title={t("End date")}
              helperText=""
              defaultDate={option.endDate ?? undefined}
              errorMessage={
                isOutsideRange("endDate")
                  ? outsideRangeMessage
                  : dateProblems.endsBeforeStart
                    ? t("The end date is before the start date")
                    : (!startLapses && (lapseReason ?? pastLastDateReason)) ||
                      refusedDeadlineBeforeStart ||
                      (refusedOverlap && !refusedOverlap.startsInside ? refusedOverlap.message : null)
              }
              onChange={(date) => onChange({ ...option, endDate: date ?? null })}
              isRequiredButNotProvided={false}
              savesTypingOnLeave
            />
            {!dateProblems.endsBeforeStart &&
              !isOutsideRange("endDate") &&
              endsNoLaterThanContract(option, contractEndDate) && (
              <Text fontSize="sm" color="smWarning" pt="1" data-testid="option-does-not-extend-contract">
                {t("The extension does not end after the contract's end date {{date}}, and cannot be exercised.", {
                  date: displayDate(contractEndDate ?? ""),
                })}
              </Text>
            )}
          </Box>
        </Box>
      ) : (
        <Box key="duration" pt="2">
          <ExtensionFields
            value={extension}
            onChange={changeExtension}
            size="sm"
            durationError={refusedDeadlineBeforeStart ?? refusedOverlap?.message ?? null}
            deadlineError={
              deadlineBeforeStart
                ? t("The deadline would be {{deadline}}, before the contract starts on {{start}}", {
                    deadline: displayDate(deadline ?? ""),
                    start: displayDate(contractStart ?? ""),
                  })
                : null
            }
          />
          <Box pt="2">
            {contractEndDate ? (
              <OptionPeriod option={shown} />
            ) : (
              <Text fontSize="sm" color="smError" data-testid="option-needs-end-date">
                {t("Set the contract's end date to calculate the dates, or set the dates yourself.")}
              </Text>
            )}
            {pastLastDateReason}
          </Box>
        </Box>
      )}
    </>
  );
};

const unusedExtensionName = (options: OptionDto[], nameFor: (number: number) => string): string => {
  const taken = new Set(options.map((option) => option.name?.trim().toLowerCase()));
  let number = options.length + 1;
  while (taken.has(nameFor(number).toLowerCase())) number += 1;
  return nameFor(number);
};
